import React from 'react';
import { Switch, Input, InputNumber } from 'antd';
import {
  SafetyCertificateOutlined, EnvironmentOutlined,
  MailOutlined, CheckSquareOutlined, ClockCircleOutlined,
  InfoCircleOutlined,
} from '@ant-design/icons';

const { TextArea } = Input;

const COLORS = {
  primary: '#1a1a2e',
  surface: '#1e1e3a',
  surfaceLight: '#252547',
  border: 'rgba(255,255,255,0.08)',
  text: '#e2e8f0',
  textSecondary: '#94a3b8',
  textMuted: '#64748b',
  emerald: '#10b981',
  emeraldBg: 'rgba(16,185,129,0.12)',
  amber: '#f59e0b',
  amberBg: 'rgba(245,158,11,0.12)',
  rose: '#ef4444',
  roseBg: 'rgba(239,68,68,0.12)',
  violet: '#8b5cf6',
  violetBg: 'rgba(139,92,246,0.12)',
  blue: '#3b82f6',
  blueBg: 'rgba(59,130,246,0.12)',
  glass: 'rgba(255,255,255,0.04)',
  glassBorder: 'rgba(255,255,255,0.08)',
};

const glassCard = {
  background: COLORS.glass,
  backdropFilter: 'blur(16px)',
  WebkitBackdropFilter: 'blur(16px)',
  border: `1px solid ${COLORS.glassBorder}`,
  borderRadius: 12,
};

const sectionStyle = {
  display: 'flex',
  justifyContent: 'space-between',
  alignItems: 'flex-start',
  padding: '16px 0',
  borderBottom: `1px solid ${COLORS.border}`,
};

const labelStyle = {
  display: 'flex',
  alignItems: 'center',
  gap: 10,
  fontWeight: 600,
  fontSize: 13,
  color: COLORS.text,
};

const descStyle = {
  fontSize: 11,
  color: COLORS.textMuted,
  marginTop: 4,
  maxWidth: 440,
  lineHeight: 1.5,
};

const ComplianceConfig = ({ compliance = {}, onChange, cooldown, onCooldownChange }) => {
  const update = (key, value) => {
    if (onChange) onChange({ ...compliance, [key]: value });
  };

  const cooldownDays = typeof cooldown === 'object' ? (cooldown?.minDaysBetween ?? 3) : (cooldown ?? 3);

  const handleCooldownChange = (value) => {
    if (onCooldownChange) {
      if (typeof cooldown === 'object') {
        onCooldownChange({ ...cooldown, minDaysBetween: value });
      } else {
        onCooldownChange(value);
      }
    }
  };

  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: 16 }}>
      {/* CAN-SPAM Info Banner */}
      <div style={{
        ...glassCard,
        padding: 14,
        display: 'flex',
        alignItems: 'flex-start',
        gap: 10,
        borderLeft: `3px solid ${COLORS.amber}`,
        background: COLORS.amberBg,
      }}>
        <InfoCircleOutlined style={{ color: COLORS.amber, fontSize: 16, marginTop: 2, flexShrink: 0 }} />
        <div>
          <div style={{ fontSize: 13, fontWeight: 700, color: COLORS.text, marginBottom: 4 }}>
            CAN-SPAM & GDPR Compliance
          </div>
          <div style={{ fontSize: 12, color: COLORS.textSecondary, lineHeight: 1.5 }}>
            Federal law requires all commercial emails to include a physical mailing address and a
            functional unsubscribe mechanism. Non-compliance can result in fines of up to $46,517
            per email under CAN-SPAM, and up to 4% of global revenue under GDPR.
          </div>
        </div>
      </div>

      {/* Settings Card */}
      <div style={{ ...glassCard, padding: '4px 20px' }}>
        {/* Physical Address */}
        <div style={sectionStyle}>
          <div>
            <div style={labelStyle}>
              <EnvironmentOutlined style={{ color: COLORS.violet, fontSize: 15 }} />
              Include Physical Address
            </div>
            <div style={descStyle}>
              Required by CAN-SPAM. Your company's physical mailing address will appear in the email footer
              of every outgoing message.
            </div>
          </div>
          <Switch
            checked={compliance.includePhysicalAddress !== false}
            onChange={(v) => update('includePhysicalAddress', v)}
            style={{ marginTop: 4, background: compliance.includePhysicalAddress !== false ? COLORS.violet : undefined }}
          />
        </div>

        {compliance.includePhysicalAddress !== false && (
          <div style={{ padding: '0 0 16px', borderBottom: `1px solid ${COLORS.border}` }}>
            <TextArea
              placeholder="e.g., BluePlanit Corp, 123 Main St, Suite 100, Dallas, TX 75201"
              value={compliance.physicalAddress || ''}
              onChange={(e) => update('physicalAddress', e.target.value)}
              rows={2}
              style={{
                borderRadius: 8,
                marginTop: 8,
                background: COLORS.surfaceLight,
                borderColor: COLORS.border,
                color: COLORS.text,
              }}
            />
          </div>
        )}

        {/* Unsubscribe Link */}
        <div style={sectionStyle}>
          <div>
            <div style={labelStyle}>
              <MailOutlined style={{ color: COLORS.emerald, fontSize: 15 }} />
              Include Unsubscribe Link
            </div>
            <div style={descStyle}>
              Always included -- required by law. Recipients can unsubscribe with one click.
              This setting cannot be disabled.
            </div>
          </div>
          <Switch
            checked
            disabled
            style={{ marginTop: 4, opacity: 0.5, background: COLORS.emerald }}
          />
        </div>

        {/* Consent Required */}
        <div style={sectionStyle}>
          <div>
            <div style={labelStyle}>
              <CheckSquareOutlined style={{ color: COLORS.amber, fontSize: 15 }} />
              Require Opt-In Consent
            </div>
            <div style={descStyle}>
              Only send to contacts with optIn = true. Strongly recommended for GDPR compliance.
              Contacts without opt-in consent will be automatically excluded from the campaign.
            </div>
          </div>
          <Switch
            checked={compliance.consentRequired || false}
            onChange={(v) => update('consentRequired', v)}
            style={{ marginTop: 4, background: compliance.consentRequired ? COLORS.amber : undefined }}
          />
        </div>

        {/* Cooldown Period */}
        <div style={{ ...sectionStyle, borderBottom: 'none' }}>
          <div>
            <div style={labelStyle}>
              <ClockCircleOutlined style={{ color: COLORS.blue, fontSize: 15 }} />
              Cooldown Period
            </div>
            <div style={descStyle}>
              Minimum number of days between emails to the same contact across ALL campaigns.
              This prevents email fatigue, reduces spam complaints, and protects your sender reputation.
            </div>
          </div>
          <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginTop: 4 }}>
            <InputNumber
              min={0}
              max={30}
              value={cooldownDays}
              onChange={handleCooldownChange}
              style={{
                width: 70,
                borderRadius: 8,
              }}
            />
            <span style={{ fontSize: 12, color: COLORS.textMuted }}>days</span>
          </div>
        </div>
      </div>

      {/* Best Practices Footer */}
      <div style={{
        ...glassCard,
        padding: 14,
        display: 'flex',
        alignItems: 'flex-start',
        gap: 10,
        borderLeft: `3px solid ${COLORS.blue}`,
        background: COLORS.blueBg,
      }}>
        <SafetyCertificateOutlined style={{ color: COLORS.blue, fontSize: 16, marginTop: 2, flexShrink: 0 }} />
        <div style={{ fontSize: 11, color: COLORS.textSecondary, lineHeight: 1.5 }}>
          <strong style={{ color: COLORS.text }}>Best Practices:</strong> Enable opt-in consent, set cooldown to
          at least 3 days, and always include your physical address. Monitor your domain health metrics to
          maintain good sender reputation. Campaigns with poor compliance settings may trigger spam filters
          and damage your domain reputation.
        </div>
      </div>
    </div>
  );
};

export default ComplianceConfig;
